import { AdaptiveRevealPacer } from "./AdaptiveRevealPacer.ts";

/**
 * The pacing surface `StreamHandler` drives, derived structurally from the one concrete
 * pacer so there's no hand-maintained interface to keep in sync. Production always uses
 * {@link AdaptiveRevealPacer}; the injection point exists only so a comparison harness
 * can supply alternative pacers that match this shape.
 */
type RevealPacer = Pick<
  AdaptiveRevealPacer,
  "tickMs" | "isStreaming" | "recordArrival" | "charsThisTick"
>;

/**
 * A char range of content that must reveal all at once — `start` inclusive,
 * `end` exclusive, like the args to `String.prototype.slice` (e.g. a link the
 * caller doesn't want shown half-formed). The handler knows nothing more about
 * what a range means, only that it never stops inside one.
 */
export type AtomicRange = { start: number; end: number };

/**
 * The end index one reveal tick should advance to: the smaller of
 * `currentLength + charsPerUpdate` and `contentLength`, snapped to an atomic
 * range's end if the stop point would land strictly inside one (so a link is
 * never revealed half-way). `atomicRanges` must be ascending, disjoint, and
 * bounded by `contentLength` (the caller filters them), so at most one range can
 * straddle the stop point. Pure — extracted so its boundary cases are testable
 * without a handler or timer.
 */
export function computeRevealEnd(
  currentLength: number,
  charsPerUpdate: number,
  contentLength: number,
  atomicRanges: readonly AtomicRange[],
): number {
  const nextChunkEnd = Math.min(currentLength + charsPerUpdate, contentLength);
  // Stopping exactly at a range's start (revealing none of it) or end is a clean
  // boundary; only a strictly-interior stop must snap forward to the range end.
  const straddled = atomicRanges.find(
    (range) => range.start < nextChunkEnd && nextChunkEnd < range.end,
  );
  return straddled === undefined ? nextChunkEnd : straddled.end;
}

/** Index of the first differing char between two strings, or the shorter length if one is a prefix of the other. */
function firstDivergenceIndex(a: string, b: string): number {
  const limit = Math.min(a.length, b.length);
  for (let i = 0; i < limit; i++) {
    if (a.charAt(i) !== b.charAt(i)) return i;
  }
  return limit;
}

/**
 * Generic streaming handler for any string field. Takes a full "content" string (doesn't
 * matter what object it's on), and streams it out to the UI.
 *
 * This has `subscribe` and `getSnapshot` functions, which are intended to integrate with React's `useSyncExternalStore`.
 *
 * The reveal is purely client side visual polish over either a complete value or
 * content that is still extending through server-driven streaming.
 *
 * Manages the buffered streaming of content for visual polish.
 * Maintains two content buffers:
 * - internalContent: The complete content received
 * - visibleContent: The content that has been gradually revealed to the UI
 *
 * Syntax-sensitive buffering is handled by backend streaming. This class only
 * smooths the rate at which received text appears.
 */
export class StreamHandler {
  private listener: (() => void) | null = null;

  private internalContent: string | null = null;
  private visibleContent = "";
  /**
   * Ranges of `internalContent` the reveal must cross in one tick, ascending
   * and disjoint (the client appends them in arrival order with growing
   * offsets). Replaced on every `setContent` from the caller's cumulative
   * list; cleared when content diverges (the offsets no longer map).
   */
  private atomicRanges: readonly AtomicRange[] = [];
  /**
   * Whether `internalContent` is the final, authoritative value. `false` while a
   * server-driven stream is still arriving — the reveal timer keeps running so
   * later extensions continue smoothly instead of stopping when momentarily
   * caught up. Defaults to `true` so the all-at-once (cosmetic smoothing) path is
   * unchanged.
   */
  private streamComplete = true;

  /**
   * Owns all reveal-rate decisions (and the loop period via {@link RevealPacer.tickMs}):
   * the simple fraction decay for the all-at-once cosmetic case, and the adaptive jitter
   * buffer once server-driven deltas arrive. `setContent` feeds it arrivals; the reveal
   * tick asks it how many chars to advance. Injectable so a comparison harness can swap
   * pacing models; the app defaults to {@link AdaptiveRevealPacer}.
   */
  private readonly pacer: RevealPacer;

  constructor(pacer: RevealPacer = new AdaptiveRevealPacer()) {
    this.pacer = pacer;
  }

  /**
   * Set the content to stream out.
   *
   * The all-at-once path calls this once with the full value (`complete` defaults
   * to true). Incremental (server-driven) streaming calls it repeatedly with the
   * growing value and `complete: false`, then once more with `complete: true` when
   * the authoritative final value is known.
   *
   * @param content the full content known so far
   * @param complete whether `content` is final. When false, the timer keeps
   *   revealing so subsequent extensions stream in smoothly.
   * @param atomicRanges the caller's full cumulative atomic ranges for
   *   `content`, ascending and disjoint. Replaces the stored set on every call
   *   (`undefined` means none); ranges past the end of `content` are dropped,
   *   which is exactly right when a shorter authoritative value truncates the
   *   streamed text — a truncated range no longer bounds a complete span.
   */
  public setContent(
    content: string,
    complete: boolean = true,
    atomicRanges?: readonly AtomicRange[],
  ): void {
    this.streamComplete = complete;

    // Either string is a prefix of the other — the normal streaming + finalize
    // path: the final value extends what we streamed (more to reveal), or is a
    // shorter prefix of it. The shorter-prefix case is a BACKSTOP — its only known
    // trigger is a forthic streaming-tokenizer artifact (a marked literal's closing
    // `'''` is emitted as content deltas until the third quote confirms the close,
    // leaving up to two stray trailing quotes the finalized string drops), which is
    // slated to be fixed upstream in forthic; absent it, finalize never undershoots
    // the stream. Either way, reconcile to `content` and keep revealing smoothly —
    // no warn, no snap.
    if (
      this.internalContent === null ||
      content === this.internalContent ||
      content.startsWith(this.internalContent) ||
      this.internalContent.startsWith(content)
    ) {
      // A real server-driven delta (not the all-at-once path, not the finalize call,
      // and not a duplicate frame that didn't grow): feed the arrival-rate / gap
      // estimator that paces the adaptive reveal. Guarding on growth keeps a repeated
      // equal-content frame from advancing the last-arrival clock and skewing the gap.
      if (!complete && content.length > (this.internalContent?.length ?? 0)) {
        this.pacer.recordArrival(content.length);
      }
      this.internalContent = content;
      this.atomicRanges = (atomicRanges ?? []).filter((range) => range.end <= content.length);
      // Only when we'd already revealed past the (now shorter) final value: trim
      // the stray tail. In the extend case visibleContent is always within content,
      // so this is a no-op there.
      if (this.visibleContent.length > content.length) {
        this.visibleContent = content;
      }
      this.notifyListener();
      return;
    }

    // Genuine divergence: neither string is a prefix of the other, so the
    // authoritative finalize corrected content mid-string (e.g. a wire-format tag
    // streamed raw then scrubbed, or an in-loop regeneration). Rare — trust the new
    // content and reveal it immediately.
    // Log shape, not bodies: the authoritative finalize scrubs wire tags, so any
    // reply that streamed a mid-message tag hits this branch routinely — dumping
    // full (potentially large, user-content) strings to the console every time.
    console.warn("StreamHandler: content diverged from streamed prefix, revealing corrected value", {
      previousLength: this.internalContent.length,
      newLength: content.length,
      divergedAt: firstDivergenceIndex(this.internalContent, content),
    });
    this.internalContent = content;
    this.visibleContent = content;
    // The corrected value is revealed whole, and the old offsets don't map
    // onto it — ranges are moot either way.
    this.atomicRanges = [];
    this.notifyListener();
  }

  /** Subscribe to streaming updates @see https://react.dev/reference/react/useSyncExternalStore
   *
   * We init streaming here, and not on `setContent` because the streaming is purely for the benefit of the UI,
   * It (via `subscribe`) decides when to start, which ensures that even if setContent is called before this,
   * we still see the full content string stream out.
   */
  public subscribe = (listener: () => void): (() => void) => {
    this.listener = listener;
    const streamingIntervalId = this.initStreaming();
    return () => {
      this.listener = null;
      clearInterval(streamingIntervalId);
    };
  };

  /** Get current snapshot @see https://react.dev/reference/react/useSyncExternalStore */
  public getSnapshot = (): string => {
    return this.visibleContent;
  };

  /** Notify all listeners of changes */
  private notifyListener = (): void => {
    this.listener?.();
  };

  /**
   * Moves the next chunk of characters from internalContent to visibleContent.
   *
   * Respects `charsPerUpdate`, revealing fewer only when less content remains —
   * except across an atomic range, which is revealed whole in one tick rather
   * than stopped inside (see {@link computeRevealEnd}).
   */
  advanceContent(charsPerUpdate: number): boolean {
    if (this.internalContent === null) return false;

    const internalLength = this.internalContent.length;
    if (!internalLength) return false;

    const currentLength = this.visibleContent.length;

    // if we're caught up, do nothing
    if (currentLength >= internalLength) return false;

    const nextChunkEnd = computeRevealEnd(
      currentLength,
      charsPerUpdate,
      internalLength,
      this.atomicRanges,
    );
    this.visibleContent = this.internalContent.slice(0, nextChunkEnd);
    return true;
  }

  /**
   * Gradually reveals buffered content to the UI at a controlled rate using a timer.
   *
   * The timer waits for the first value, reveals buffered characters, and stops
   * only after the final value is fully visible. A caught-up live stream keeps
   * ticking so later extensions use the same reveal loop.
   */
  private initStreaming() {
    const intervalId = setInterval(() => {
      // Wait for the first value without stopping the timer.
      const content = this.internalContent;
      if (content === null) return;

      // A caught-up live stream keeps ticking; a completed one can stop.
      const remaining = content.length - this.visibleContent.length;
      if (remaining <= 0 && this.streamComplete) {
        clearInterval(intervalId);
        return;
      }

      const updated = this.advanceContent(this.pacer.charsThisTick(remaining, this.streamComplete));
      if (updated) this.notifyListener();
    }, this.pacer.tickMs);

    return intervalId;
  }
}
